import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment/min/moment-with-locales';
import { Button } from '@scramble/ui';
import { DATE_FORMAT_D_MMMM_YYYY, DATE_REQUEST } from '@/helpers/common';
import { APP_LINKS } from '@/helpers/links';
import { prettyDate } from '@/helpers/utils';
import { prettyMoney } from '@scramble/utils';
import { getTimeLeftMessage } from '@/helpers/date';
import { useGetMe } from '@/hooks/useGetMe';
import { useRoundStatus } from '@/hooks/shared/useRoundStatus';
import { useEarlyReservation } from '@/hooks/shared/useEarlyReservation';
import { useGetFundraisingDates } from '@/query/queries/investing/useGetFundraisingDates';
import IconClock from '@scramble/design-system/icons/clock-rewind.svg?react';
import IconCalendar from '@scramble/design-system/icons/calendar.svg?react';
import IconEdit from '@scramble/design-system/icons/edit-03.svg?react';
import IconJoin from '@scramble/design-system/icons/arrow-circle-right.svg?react';
import IconAdd from '@scramble/design-system/icons/plus-circle.svg?react';
import s from './index.module.scss';

export const RoundInformationStatus = ({ onJoin }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canInvest } = useGetMe();
  const { status, isParticipating } = useRoundStatus();
  const { data: fundraisingDates } = useGetFundraisingDates();
  const {
    reservedAmount,
    loaded: isLoadedReservation,
  } = useEarlyReservation();

  if (!fundraisingDates || !status) return null;

  const isActive = !!fundraisingDates.is_active;
  const period = `${prettyDate(fundraisingDates.start_date, 'D')}-${prettyDate(fundraisingDates.end_date, 'D MMMM')}`;

  const timeLeft = getTimeLeftMessage(
    {
      targetDate: isActive
        ? moment(fundraisingDates?.end_date).add(1, 'day').format(DATE_REQUEST)
        : fundraisingDates?.start_date,
      hoursOffset: isActive ? 0 : 12,
    },
    t,
  );

  const showReservation = !!(isLoadedReservation && reservedAmount && status.is_active);

  return (
    <div className={s.wrapper}>
      <div className={classnames(s.round, showReservation && s.round_reserve)}>
        <div className={s.left}>
          <div className={s.content}>
            <div className="f-headline-2xs f-500">
              {isActive
                ? t('investing:round_info.active')
                : t('investing:round_info.coming')}
            </div>
            <div className="f-body-md">
              {isActive
                ? isParticipating
                  ? t('investing:round_info.you_can_modify', {
                      date: moment(status.end_date)
                        .add(1, 'day')
                        .format(DATE_FORMAT_D_MMMM_YYYY),
                    })
                  : t('investing:round_info.join_and_allocate')
                : t('investing:round_info.add_cash_be_ready')}
            </div>
          </div>
          <div className={s.info}>
            <div className={s.info_item}>
              <IconClock />
              <span className="f-body-sm f-500">{timeLeft}</span>
            </div>
            <div className={s.info_item}>
              <IconCalendar />
              <span className="f-body-sm f-500">{period}</span>
            </div>
          </div>
        </div>
        <div className={s.right}>
          {isActive &&
            (isParticipating ? (
              <Button
                theme="neutral"
                onClick={onJoin}
                size="lg"
                appearance="filled"
                disabled={!canInvest}
                className={s.btn_edit}
              >
                <IconEdit />
                <span className="f-body-md f-500">
                  {t('investing:round_info.edit')}
                </span>
              </Button>
            ) : (
              <Button
                theme="brand"
                onClick={onJoin}
                size="lg"
                appearance="filled"
                disabled={!canInvest}
                className={s.btn_join}
              >
                <IconJoin />
                <span className="f-body-md f-500">
                  {t('investing:round_info.join_now')}
                </span>
              </Button>
            ))}
          {!isActive && (
            <Button
              theme="neutral"
              onClick={() => navigate(APP_LINKS.addFunds)}
              size="lg"
              appearance="filled"
              className={s.btn_add}
            >
              <IconAdd />
              <span className="f-body-md f-500">
                {t('investing:round_info.add_cash')}
              </span>
            </Button>
          )}
        </div>
      </div>

      {showReservation && (
        <div className={s.reservation}>
          <div className={s.reservation_left}>
            <div className="f-body-md">
              {t('investing:early_reservation.reserved_amount')}
            </div>
            <div className="f-body-sm">
              {t('investing:early_reservation.available_until', {
                time: '23:59',
                date: prettyDate(status?.reservation_expires_at, DATE_FORMAT_D_MMMM_YYYY),
              })}
            </div>
          </div>
          <div className="f-body-md f-500">{prettyMoney(reservedAmount)}</div>
        </div>
      )}
    </div>
  );
};
